import React, { useMemo, useCallback, useEffect } from 'react';
import { StyleSheet, View, Platform, useWindowDimensions } from 'react-native';
import { MasonryFlashList } from '@shopify/flash-list';
import { useTheme } from '@/context/ThemeContext';
import { useFileStore, useLightboxStore, useModelStore, useChatInputStore, useAuthStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import StatusContainer from '@/components/StatusContainer';
import MediaCardLarge from '@/components/ChatInput/MediaCardLarge';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import useRequireAuth from '@/hooks/useRequireAuth';
import { useRouter } from 'expo-router';
import { BORDER_RADIUS, DEFAULT_IMAGE_MODEL_SLUG } from '@/constants/config';
import { resolveConfiguredOutputModel } from '@/lib/defaultOutputModels';

const createStyles = (colors, isXS) => StyleSheet.create({
  container: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: isXS ? 12 : 18,
    paddingBottom: 20,
  },
  emptyContainer: {
    paddingVertical: 60,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: isXS ? 16 : 24,
  },
  loadingContainer: {
    paddingHorizontal: isXS ? 16 : 24,
    paddingTop: 8,
  },
  loadingGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: isXS ? 8 : 12,
  },
  loadingItem: {
    borderRadius: BORDER_RADIUS.medium || 12,
    backgroundColor: colors.solid_color_secondary,
  },
  itemWrapper: {
    padding: isXS ? 4 : 6,
  },
  itemContainer: {
    borderRadius: BORDER_RADIUS.medium || 12,
    overflow: 'hidden',
  },
  footerLoader: {
    paddingVertical: 20,
    alignItems: 'center',
  },
});

const ImageGalleryItem = React.memo(({ file, onPress, isXS }) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);
  
  // Calculate aspect ratio from image metadata, default to 1:1 for images
  const imageWidth = file?.metadata?.image?.width || 1024;
  const imageHeight = file?.metadata?.image?.height || 1024;
  const aspectRatio = imageWidth / imageHeight;
  
  return (
    <View style={styles.itemWrapper}>
      <View style={[styles.itemContainer, { aspectRatio }]}>
        <MediaCardLarge
          fillContainer
          file={file}
          onPress={onPress}
          isDownloadable
        />
      </View>
    </View>
  );
});

const ImageGallery = () => {
  const { colors } = useTheme();
  const { isXS, isXL } = useResponsiveness();
  const { t } = useTranslation();
  const router = useRouter();
  const { width: windowWidth } = useWindowDimensions();
  
  const { openLightbox } = useLightboxStore(state => state.actions);
  
  // File store
  const files = useFileStore(state => state.files);
  const generatedImageIds = useFileStore(state => state.generatedImageIds);
  const isLoading = useFileStore(state => state.isLoading);
  const errors = useFileStore(state => state.errors);
  const pagination = useFileStore(state => state.pagination);
  
  const { fetchGeneratedImages } = useFileStore(state => state.actions);
  const userId = useAuthStore(state => state.user?.id);
  const hasUser = Boolean(userId);
  const requireAuth = useRequireAuth();

  const numColumns = useMemo(() => {
    if (isXS) return 2;
    if (isXL) return 4;
    return 3;
  }, [isXS, isXL]);

  const gap = isXS ? 8 : 12;
  const horizontalPadding = isXS ? 12 : 18;
  const columnWidth = useMemo(() => {
    const totalGaps = (numColumns - 1) * gap;
    const availableWidth = windowWidth - (horizontalPadding * 2) - totalGaps;
    return availableWidth / numColumns;
  }, [windowWidth, numColumns, gap, horizontalPadding]);

  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);

  // The "My Images" gallery is per-user. Skip the fetch entirely for
  // anonymous visitors so they don't trip a 401, and render a sign-in
  // CTA empty state below in place of the gallery.
  useEffect(() => {
    if (hasUser) fetchGeneratedImages(1);
  }, [fetchGeneratedImages, hasUser]);

  // Get the image files from IDs
  const imageFiles = useMemo(() => {
    return generatedImageIds.map(id => files[id]).filter(Boolean);
  }, [files, generatedImageIds]);

  const handleImagePress = useCallback((file) => {
    if (file?.cdnPath) {
      openLightbox({
        imageUrl: file.cdnPath,
        fileId: file.id,
        openToEditor: false,
        withInpainting: true,
        withChatInput: true,
      });
    }
  }, [openLightbox]);

  const handleLoadMore = useCallback(async () => {
    const currentPagination = pagination.generatedImages;
    if (!currentPagination?.hasMore || isLoading) return;
    
    const nextPage = (currentPagination?.page || 1) + 1;
    await fetchGeneratedImages(nextPage);
  }, [pagination, isLoading, fetchGeneratedImages]);

  const handleCreateFirst = useCallback(async () => {
    // Anonymous visitors get the unified auth CTA — the underlying
    // generation pipeline writes to the user's account.
    const ran = requireAuth(() => true, {
      source: 'images_gallery_create_first',
      title: 'Sign in to make an image',
      subtitle: 'Image generations are billed to your account so we can apply your credits and save the result.',
      icon: 'image',
    });
    if (ran !== true) return;
    // Resolve the latest admin-configured create-image default. The constant
    // is only a fallback when the intent-engine setting is not configured.
    const { model: imageModel } = await resolveConfiguredOutputModel(
      useModelStore.getState(),
      'create-image',
      DEFAULT_IMAGE_MODEL_SLUG,
      { force: true },
    );
    
    if (imageModel?.id) {
      // Initialize image generation with the model pre-selected
      useChatInputStore.getState().initializeImageGeneration('', imageModel.id, false);
    }
    
    router.push('/app');
  }, [router, requireAuth]);

  const renderItem = useCallback(({ item }) => (
    <ImageGalleryItem
      file={item}
      onPress={handleImagePress}
      isXS={isXS}
    />
  ), [handleImagePress, isXS]);

  const keyExtractor = useCallback((item) => item.id, []);

  const getItemType = useCallback((item) => {
    // Use aspect ratio to group similar items for better recycling
    const imageWidth = item?.metadata?.image?.width || 1024;
    const imageHeight = item?.metadata?.image?.height || 1024;
    const ratio = imageWidth / imageHeight;
    if (ratio > 1.2) return 'landscape';
    if (ratio < 0.8) return 'portrait';
    return 'square';
  }, []);

  const overrideItemLayout = useCallback((layout, item) => {
    // Estimate item height based on aspect ratio for better masonry layout
    const imageWidth = item?.metadata?.image?.width || 1024;
    const imageHeight = item?.metadata?.image?.height || 1024;
    const aspectRatio = imageWidth / imageHeight;
    layout.size = columnWidth / aspectRatio;
  }, [columnWidth]);

  const ListFooterComponent = useCallback(() => {
    if (!pagination?.generatedImages?.hasMore) return null;
    return (
      <View style={styles.footerLoader}>
        <Text size="s" color="secondary">Loading more...</Text>
      </View>
    );
  }, [pagination, styles.footerLoader]);

  // Show loading state
  if (isLoading && imageFiles.length === 0) {
    const loadingItemWidth = columnWidth;
    return (
      <View style={styles.loadingContainer}>
        <View style={styles.loadingGrid}>
          {[...Array(6)].map((_, index) => (
            <View
              key={index}
              style={[
                styles.loadingItem,
                {
                  width: loadingItemWidth,
                  aspectRatio: 1,
                }
              ]}
            />
          ))}
        </View>
      </View>
    );
  }

  // Show error state
  if (errors?.generatedImages) {
    return (
      <View style={styles.emptyContainer}>
        <StatusContainer
          type="error"
          message={errors.generatedImages}
          actionText="Retry"
          onAction={() => fetchGeneratedImages(1)}
        />
      </View>
    );
  }

  // Show empty state — anonymous visitors get a sign-in copy variant so
  // it's clear why their gallery is empty (vs "you haven't made any
  // images yet" for an authed user with an empty roll).
  if (imageFiles.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Icon 
          name="image" 
          size={48} 
          color={colors.text_color_tertiary} 
          style={{ marginBottom: 16 }}
        />
        <Text size="m" weight="medium" color="secondary" style={{ marginBottom: 4 }}>
          {hasUser ? t('images.noImages') : 'Sign in to see your images'}
        </Text>
        <Text size="s" color="tertiary" style={{ marginBottom: 20, textAlign: 'center' }}>
          {hasUser
            ? t('images.noImagesSubtitle')
            : 'Create a free account so we can save every image you generate to your gallery.'}
        </Text>
        <Button
          text={hasUser ? t('images.createFirst') : 'Sign up free'}
          variant="primary"
          size="medium"
          prependIcon={hasUser ? 'sparkles' : 'user-plus'}
          onPress={handleCreateFirst}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <MasonryFlashList
        data={imageFiles}
        numColumns={numColumns}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        getItemType={getItemType}
        overrideItemLayout={overrideItemLayout}
        estimatedItemSize={columnWidth}
        contentContainerStyle={styles.listContent}
        onEndReached={handleLoadMore}
        onEndReachedThreshold={0.5}
        ListFooterComponent={ListFooterComponent}
        scrollEnabled={Platform.OS !== 'macos'}
        showsVerticalScrollIndicator={false}
        optimizeItemArrangement
      />
    </View>
  );
};

export default React.memo(ImageGallery);
